import * as polygonClipping from "polygon-clipping";
import * as THREE from "three";
import { WallFaceSide, WallType } from "../types/floorplannerTypes";
import type { CatalogSurfaceReference } from "../features/floorplan3d/products/product3DTypes";
import { getBuiltInFurnitureMaterial } from "../features/floorplan3d/materials/builtInFurnitureMaterials";

type Pair = [number, number];
type Ring = Pair[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

export type WallFaceAppearance = {
  surfaceRef?: CatalogSurfaceReference;
  fillColor?: number;
  /** True when fillColor came from a face override (not wall.fillColor). */
  fillColorIsOverride?: boolean;
};

/**
 * Left-facing unit normal in plan space for a straight/arc wall (start→end).
 * Matches the local-frame +Y used by straight wall 3D geometry.
 */
export function getWallLeftNormal(wall: WallType): THREE.Vector2 | null {
  const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
  if (dir.lengthSq() < 1e-12) return null;
  dir.normalize();
  return new THREE.Vector2(-dir.y, dir.x);
}

/**
 * Build a half-width plan shape for one broad face of a straight wall.
 * Left = centerline → +leftNormal; right = centerline → -leftNormal.
 * Prefer {@link clipShapesToWallSide} when starting from composed fill geometry
 * so miters / cutouts stay correct.
 */
export function buildWallHalfBandShape(
  wall: WallType,
  side: WallFaceSide,
  wallWidth: number,
): THREE.Shape | null {
  const leftNormal = getWallLeftNormal(wall);
  if (!leftNormal) return null;

  const half = Math.max(wallWidth, 1e-4) * 0.5;
  const outerSign = side === "left" ? 1 : -1;
  const a = wall.start.clone();
  const b = wall.end.clone();
  const aOuter = a.clone().addScaledVector(leftNormal, outerSign * half);
  const bOuter = b.clone().addScaledVector(leftNormal, outerSign * half);

  const shape = new THREE.Shape();
  // Winding so the half-band faces upward in plan (z+), matching extrude norms.
  if (side === "left") {
    shape.moveTo(a.x, a.y);
    shape.lineTo(b.x, b.y);
    shape.lineTo(bOuter.x, bOuter.y);
    shape.lineTo(aOuter.x, aOuter.y);
  } else {
    shape.moveTo(a.x, a.y);
    shape.lineTo(aOuter.x, aOuter.y);
    shape.lineTo(bOuter.x, bOuter.y);
    shape.lineTo(b.x, b.y);
  }
  shape.closePath();
  return shape;
}

const shapeToPolygon = (shape: THREE.Shape): Polygon | null => {
  const points = shape.getPoints();
  if (points.length < 3) return null;
  const ring: Ring = points.map((point) => [point.x, point.y]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (!first || !last) return null;
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  const holes: Ring[] = shape.holes
    .map((hole) => {
      const holePoints = hole.getPoints();
      if (holePoints.length < 3) return null;
      const holeRing: Ring = holePoints.map((point) => [point.x, point.y]);
      const holeFirst = holeRing[0];
      const holeLast = holeRing[holeRing.length - 1];
      if (!holeFirst || !holeLast) return null;
      if (holeFirst[0] !== holeLast[0] || holeFirst[1] !== holeLast[1]) {
        holeRing.push([holeFirst[0], holeFirst[1]]);
      }
      return holeRing;
    })
    .filter((hole): hole is Ring => hole !== null);
  return [ring, ...holes];
};

const multiPolygonToShapes = (multiPolygon: MultiPolygon): THREE.Shape[] => {
  const shapes: THREE.Shape[] = [];
  multiPolygon.forEach((polygon) => {
    if (!polygon[0] || polygon[0].length < 4) return;
    const outer = polygon[0].slice(0, -1).map(([x, y]) => new THREE.Vector2(x, y));
    if (outer.length < 3) return;
    const shape = new THREE.Shape(outer);
    for (let i = 1; i < polygon.length; i++) {
      const holeRing = polygon[i];
      if (!holeRing || holeRing.length < 4) continue;
      shape.holes.push(
        new THREE.Path(
          holeRing.slice(0, -1).map(([x, y]) => new THREE.Vector2(x, y)),
        ),
      );
    }
    shapes.push(shape);
  });
  return shapes;
};

/**
 * Clip already-composed wall fill shapes to one side of the centerline.
 * Preserves miters, cutouts, and prior clipping by intersecting the composed
 * fill with an oversized half-plane rather than rebuilding from endpoints.
 */
export function clipShapesToWallSide(
  shapes: THREE.Shape[],
  wall: WallType,
  side: WallFaceSide,
  wallWidth: number,
): THREE.Shape[] {
  const leftNormal = getWallLeftNormal(wall);
  if (!leftNormal || shapes.length === 0) return [];

  const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
  const length = dir.length();
  if (length < 1e-8) return [];
  dir.multiplyScalar(1 / length);

  // Extend past endpoints / thickness so mitered joints stay inside the half-plane.
  const margin = Math.max(wallWidth * 4, 0.5);
  const extent = Math.max(wallWidth * 4, 0.5);
  const mid = wall.start.clone().lerp(wall.end, 0.5);
  const along = dir.clone().multiplyScalar(length * 0.5 + margin);
  const outward = leftNormal
    .clone()
    .multiplyScalar(side === "left" ? extent : -extent);
  // Tiny bias keeps the shared centerline on this side without a gap.
  const bias = leftNormal
    .clone()
    .multiplyScalar(side === "left" ? 1e-4 : -1e-4);

  const a = mid.clone().sub(along).add(bias);
  const b = mid.clone().add(along).add(bias);
  const c = b.clone().add(outward);
  const d = a.clone().add(outward);
  const halfPlane: Polygon = [
    [
      [a.x, a.y],
      [b.x, b.y],
      [c.x, c.y],
      [d.x, d.y],
      [a.x, a.y],
    ],
  ];

  const subject = shapes
    .map(shapeToPolygon)
    .filter((polygon): polygon is Polygon => polygon !== null);
  if (subject.length === 0) return [];

  try {
    const clipped = polygonClipping.intersection(
      subject,
      halfPlane,
    ) as MultiPolygon;
    if (!Array.isArray(clipped) || clipped.length === 0) return [];
    return multiPolygonToShapes(clipped);
  } catch {
    return [];
  }
}

/** Classic plaster fill used when a wall has no explicit fillColor (2D + 3D). */
export const DEFAULT_WALL_FILL_COLOR = 0xd8d0c4;

/**
 * Neutral gray for wall tops / end caps / opening structure when the two
 * broad faces use different finishes, so the thickness does not look like
 * only Side 1.
 */
export const WALL_STRUCTURE_NEUTRAL_FILL = 0xb0b0b0;

/**
 * Kill switch for per-side wall finishes, set at build time via
 * REACT_APP_WALL_SIDE_COLORS:
 * - unset / "on": feature enabled
 * - "hidden": toggle hidden, existing per-side walls still render per side
 * - "off": toggle hidden and every wall renders as Side 1 on both faces
 *   (saved data is left untouched)
 */
const wallSideColorsMode = (): "on" | "hidden" | "off" => {
  const raw = process.env.REACT_APP_WALL_SIDE_COLORS?.trim().toLowerCase();
  if (raw === "off" || raw === "hidden") return raw;
  return "on";
};

export const isWallSideColorsToggleEnabled = (): boolean =>
  wallSideColorsMode() === "on";

const isWallSideColorsRenderEnabled = (): boolean =>
  wallSideColorsMode() !== "off";

/**
 * Whole-wall fields mirror Side 1 while per-side mode is on, so clients
 * without this feature (or a code rollback) render Side 1 on both faces.
 * A mismatch means a client that ignores faceMaterials edited the wall
 * afterwards; its whole-wall edit wins and the stale face data is ignored.
 */
export function wallSideOneMirrorIsInSync(wall: WallType): boolean {
  const left = wall.threeD?.faceMaterials?.left;
  if (left?.fillColor !== undefined && wall.fillColor !== left.fillColor) {
    return false;
  }
  return left?.surfaceRef?.id === wall.threeD?.surfaceRef?.id;
}

/** True when the wall uses independent Side 1 / Side 2 face overrides. */
export function wallHasPerSideFaceMaterials(wall: WallType): boolean {
  const hasFaceData = Boolean(
    wall.threeD?.differentSideColors ||
      wall.threeD?.faceMaterials?.left ||
      wall.threeD?.faceMaterials?.right,
  );
  return (
    hasFaceData &&
    isWallSideColorsRenderEnabled() &&
    wallSideOneMirrorIsInSync(wall)
  );
}

/**
 * Copy Side 1's material onto the whole-wall surfaceRef / materialId.
 * materialId is only kept for Ready presets, matching whole-wall catalog applies.
 */
export function withSideOneMirror(
  threeD: NonNullable<WallType["threeD"]>,
): NonNullable<WallType["threeD"]> {
  const {
    surfaceRef: _previousSurfaceRef,
    materialId: _previousMaterialId,
    ...rest
  } = threeD;
  const sideOneRef = threeD.faceMaterials?.left?.surfaceRef;
  if (!sideOneRef) return rest;
  return {
    ...rest,
    surfaceRef: sideOneRef,
    ...(getBuiltInFurnitureMaterial(sideOneRef.id)
      ? { materialId: sideOneRef.id }
      : {}),
  };
}

/**
 * Resolve surface + fill for one full wall side.
 * In per-side mode the face owns its material — no fallback to wall.threeD.surfaceRef.
 * Otherwise face data is ignored and the whole-wall finish applies.
 */
export function resolveWallSideAppearance(
  wall: WallType,
  side: WallFaceSide,
): WallFaceAppearance {
  if (!wallHasPerSideFaceMaterials(wall)) {
    return {
      surfaceRef: wall.threeD?.surfaceRef,
      fillColor: wall.fillColor,
      fillColorIsOverride: false,
    };
  }
  const face = wall.threeD?.faceMaterials?.[side];
  const fillColorIsOverride = face?.fillColor !== undefined;
  return {
    surfaceRef: face?.surfaceRef,
    fillColor: fillColorIsOverride ? face.fillColor : wall.fillColor,
    fillColorIsOverride,
  };
}

/** Alias used by fill / materials code paths. */
export function resolveWallFaceAppearance(
  wall: WallType,
  side: WallFaceSide,
): WallFaceAppearance {
  return resolveWallSideAppearance(wall, side);
}

/**
 * Fill color for wall tops, end caps, and other non-sided structure in 3D.
 * When both faces share one finish, use that color; when they differ, use a
 * neutral gray so the top strip does not favour Side 1.
 */
export function resolveWallCapFillColor(wall: WallType): number {
  if (!wallHasPerSideFaceMaterials(wall)) {
    return wall.fillColor ?? DEFAULT_WALL_FILL_COLOR;
  }

  const leftFace = wall.threeD?.faceMaterials?.left?.fillColor;
  const rightFace = wall.threeD?.faceMaterials?.right?.fillColor;
  const leftSurfaceId = wall.threeD?.faceMaterials?.left?.surfaceRef?.id;
  const rightSurfaceId = wall.threeD?.faceMaterials?.right?.surfaceRef?.id;

  const fillsDiffer =
    leftFace !== undefined &&
    rightFace !== undefined &&
    leftFace !== rightFace;
  const surfacesDiffer = leftSurfaceId !== rightSurfaceId;

  if (fillsDiffer || surfacesDiffer) {
    return WALL_STRUCTURE_NEUTRAL_FILL;
  }
  if (leftFace !== undefined) {
    return leftFace;
  }
  if (rightFace !== undefined) {
    return rightFace;
  }
  return wall.fillColor ?? DEFAULT_WALL_FILL_COLOR;
}

export function resolveWallFaceSurfaceRef(
  wall: WallType,
  side: WallFaceSide,
): CatalogSurfaceReference | undefined {
  return resolveWallSideAppearance(wall, side).surfaceRef;
}

/**
 * Upsert or clear material / fill for one full wall side.
 * Pass null for surfaceRef or fillColor to clear that field.
 */
export function upsertWallFaceMaterial(
  wall: WallType,
  side: WallFaceSide,
  patch: {
    surfaceRef?: CatalogSurfaceReference | null;
    fillColor?: number | null;
  },
): NonNullable<WallType["threeD"]> {
  const previous = wall.threeD?.faceMaterials?.[side];
  const nextSurfaceRef =
    patch.surfaceRef === null
      ? undefined
      : patch.surfaceRef !== undefined
        ? patch.surfaceRef
        : previous?.surfaceRef;
  const nextFillColor =
    patch.fillColor === null
      ? undefined
      : patch.fillColor !== undefined
        ? patch.fillColor
        : previous?.fillColor;

  const hasOverride =
    nextSurfaceRef !== undefined || nextFillColor !== undefined;

  const nextFaceMaterials = {
    ...wall.threeD?.faceMaterials,
    [side]: hasOverride
      ? {
          surfaceRef: nextSurfaceRef,
          fillColor: nextFillColor,
        }
      : undefined,
  };
  const cleanedFace =
    nextFaceMaterials.left || nextFaceMaterials.right
      ? {
          left: nextFaceMaterials.left,
          right: nextFaceMaterials.right,
        }
      : undefined;

  return {
    ...wall.threeD,
    differentSideColors: true,
    faceMaterials: cleanedFace,
  };
}

/**
 * Clear all per-side overrides so the wall uses whole-wall fill/surface again.
 */
export function clearWallFaceMaterials(
  wall: WallType,
): NonNullable<WallType["threeD"]> {
  const {
    differentSideColors: _removedDifferentSideColors,
    faceMaterials: _removedFaces,
    ...rest
  } = wall.threeD ?? {};
  return { ...rest };
}
